/**
 * chatSound.ts — LINE ページ（/line）の受信音（2026-10-26・booking-platform/md/customer-chat.md §13-5）
 *
 * LP 管理画面（/admin）の通知音（ON/OFF・サウンドチェック・「音が鳴らない場合は画面を一度クリック」）を移したもの。
 * LINE・予約チャット・匿名チャット（要対応）の新着は、すべて soundUtils.playNotificationSound → ここを通る（同じ音）。
 *
 * - ブラウザの自動再生の制限: AudioContext はユーザー操作の前は suspended のまま。1 つの AudioContext を使い回し、
 *   最初のクリック／キー操作で resume する（installChatSoundUnlock）。鳴らせなかったら「ブロック中」を記録し、
 *   画面に案内を出す（無言で鳴らないままにしない）。
 * - タブが裏にあっても鳴らす（AudioContext が running なら鳴る）。裏のときはタブのタイトルに「(新着) 」を付け、戻ったら消す。
 * - 同じ瞬間に複数の経路（LINE の Pusher・自社チャットの一覧更新）から呼ばれても 1.5 秒に 1 回だけ鳴らす。
 * - ON/OFF は端末ごと（localStorage `line_sound_enabled`・既定 ON）。
 */
import { logEvent } from './eventLog';

export const CHAT_SOUND_KEY = 'line_sound_enabled';
export const CHAT_SOUND_THROTTLE_MS = 1500;
export const UNREAD_TITLE_PREFIX = '(新着) ';

type Listener = () => void;
let ctx: AudioContext | null = null;
let blocked = false;
let lastPlayedAt = 0;
let listeners: Listener[] = [];
let unlockInstalled = false;
let titleMarked = false;

const notify = () => listeners.forEach((l) => l());

export const subscribeChatSound = (l: Listener): (() => void) => {
  listeners.push(l);
  return () => {
    listeners = listeners.filter((x) => x !== l);
  };
};

export const isChatSoundEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(CHAT_SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
};

export const setChatSoundEnabled = (on: boolean): void => {
  try {
    window.localStorage.setItem(CHAT_SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* 保存できなくても今回の画面では切り替わる */
  }
  notify();
};

/** 音を出せない状態か（ユーザー操作の前で AudioContext が止まっている／作れない） */
export const isChatSoundBlocked = (): boolean => blocked;

const getCtx = (): AudioContext | null => {
  if (ctx && ctx.state !== 'closed') return ctx;
  try {
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return null;
    ctx = new AudioCtx() as AudioContext;
    return ctx;
  } catch (e) {
    logEvent({ action: 'chat_sound.context', result: 'fail', err: e });
    return null;
  }
};

const setBlocked = (v: boolean) => {
  if (blocked === v) return;
  blocked = v;
  notify();
};

/** ユーザー操作のときに呼ぶ（AudioContext を有効にする） */
export const unlockChatSound = async (): Promise<boolean> => {
  const c = getCtx();
  if (!c) return false;
  try {
    if (c.state === 'suspended') await c.resume();
  } catch (e) {
    logEvent({ action: 'chat_sound.unlock', result: 'fail', err: e });
  }
  const ok = c.state === 'running';
  setBlocked(!ok);
  return ok;
};

/** 最初のクリック／キー操作で音を有効にする（1 回だけ登録） */
export const installChatSoundUnlock = (): void => {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const handler = () => {
    unlockChatSound();
  };
  window.addEventListener('pointerdown', handler, true);
  window.addEventListener('keydown', handler, true);
  // 最初から許可されているブラウザ（以前に操作したサイト等）なら案内を出さない
  const c = getCtx();
  setBlocked(!c || c.state !== 'running');
};

const tone = (c: AudioContext, freq: number, start: number, duration: number, volume = 0.1) => {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, start);
  osc.frequency.exponentialRampToValueAtTime(freq * 1.1, start + duration);
  g.gain.setValueAtTime(volume, start);
  g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(start);
  osc.stop(start + duration);
};

/** 受信音そのもの（従来の playAlertSound と同じ A5 + E6） */
const ring = (c: AudioContext) => {
  tone(c, 880, c.currentTime, 0.3);
  tone(c, 1318.51, c.currentTime + 0.05, 0.3);
};

/** 裏のタブならタイトルに「(新着) 」を付け、表に戻ったら外す */
export const markTitleUnread = (): void => {
  if (typeof document === 'undefined' || !document.hidden || titleMarked) return;
  titleMarked = true;
  if (!document.title.startsWith(UNREAD_TITLE_PREFIX)) document.title = UNREAD_TITLE_PREFIX + document.title;
  const clear = () => {
    if (document.hidden) return;
    titleMarked = false;
    if (document.title.startsWith(UNREAD_TITLE_PREFIX)) document.title = document.title.slice(UNREAD_TITLE_PREFIX.length);
    document.removeEventListener('visibilitychange', clear);
  };
  document.addEventListener('visibilitychange', clear);
};

/**
 * 新着の受信音。鳴らしたら true。OFF・連続（1.5 秒以内）は鳴らさない。
 * 音を出せない（ユーザー操作の前）ときは「ブロック中」にして画面に案内を出す。
 */
export const playChatNotification = (now: number = Date.now()): boolean => {
  markTitleUnread();
  if (!isChatSoundEnabled()) return false;
  if (now - lastPlayedAt < CHAT_SOUND_THROTTLE_MS) return false;
  const c = getCtx();
  if (!c) return false;
  if (c.state !== 'running') {
    // ユーザー操作の外では resume できないことが多い。試して、だめなら案内を出す
    c.resume().catch(() => undefined);
    if ((c.state as string) !== 'running') {
      setBlocked(true);
      return false;
    }
  }
  try {
    ring(c);
    lastPlayedAt = now;
    setBlocked(false);
    return true;
  } catch (e) {
    logEvent({ action: 'chat_sound.play', result: 'fail', err: e });
    return false;
  }
};

/** サウンドチェック（ボタンから＝ユーザー操作の中なので有効化もできる）。ON/OFF・連続の制限は見ない */
export const playChatSoundCheck = async (): Promise<boolean> => {
  const ok = await unlockChatSound();
  const c = getCtx();
  if (!ok || !c) return false;
  try {
    ring(c);
    return true;
  } catch (e) {
    logEvent({ action: 'chat_sound.check', result: 'fail', err: e });
    return false;
  }
};

/** テスト用 */
export const __resetChatSound = () => {
  ctx = null;
  blocked = false;
  lastPlayedAt = 0;
  listeners = [];
  unlockInstalled = false;
  titleMarked = false;
};
